import React, { useEffect, useRef } from "react";
import { Text, StyleSheet, Animated, TouchableOpacity } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";

interface ValidationErrorBannerProps {
  errorMessage: string;
  onPress?: () => void;
}

const ValidationErrorBanner: React.FC<ValidationErrorBannerProps> = ({
  errorMessage,
  onPress,
}) => {
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    // First fade in the banner
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 300,
      useNativeDriver: true,
    }).start(() => {
      // Then start the pulsing animation
      const pulse = Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1.05, // Scale up slightly
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
      ]);

      // Repeat the pulse animation 3 times
      Animated.loop(pulse, { iterations: 3 }).start();
    });

    return () => {
      pulseAnim.setValue(1); // Reset animations on unmount
      fadeAnim.setValue(0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorMessage]);
  return (
    <TouchableOpacity
      activeOpacity={0.8}
      onPress={onPress}
      style={styles.touchable}
      accessible={true}
      accessibilityRole="button"
      accessibilityLabel={`Error: ${errorMessage}. Tap to scroll to top.`}
      accessibilityHint="Tapping this will scroll to the top of the form"
      testID="validation-banner-touchable"
    >
      <Animated.View
        testID="validation-banner-container"
        style={[
          styles.container,
          {
            opacity: fadeAnim,
            transform: [{ scale: pulseAnim }],
          },
        ]}
      >
        <MaterialIcons
          name="error-outline"
          size={24}
          color="#721c24"
          style={styles.icon}
        />
        <Text style={styles.errorText}>{errorMessage}</Text>
        <MaterialIcons
          name="keyboard-arrow-up"
          size={24}
          color="#721c24"
          style={styles.scrollIcon}
        />
      </Animated.View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  touchable: {
    width: "100%",
    marginBottom: 16,
  },
  container: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#f8d7da",
    borderColor: "#f5c6cb",
    borderWidth: 1,
    borderLeftWidth: 5,
    borderLeftColor: "#dc3545",
    borderRadius: 6,
    padding: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 4,
  },
  icon: {
    marginRight: 10,
  },
  scrollIcon: {
    marginLeft: 10,
  },
  errorText: {
    color: "#721c24",
    fontSize: 15,
    flex: 1,
    fontWeight: "500",
  },
});

export default ValidationErrorBanner;
